/*
  Toggle class on another element.

  Options:
    classToToggle: The class to toggle
    target: The element the class will be applied to. 'body' is default
    onHover: Open on hover instead on click
    hoverDelay: Delay after hovering
    ariaExpanded: For accessibility purposes set this when you are displaying a modal

  HTML Usage:
  <class-toggle-component data-options='{}'>
    <button>Click me</button>
  </class-toggle-component>
*/

class ClassToggleComponent extends HTMLElement {
  constructor() {
    super();
  }
  connectedCallback() {
    // Options
    this.options = {
      classToToggle: 'toggle--is-active',
      target: 'body',
      onHover: false,
      hoverDelay: 0,
      ariaExpanded: false,
    };

    this.preventBackgroundScroll = false;

    // Get options from element data and combine with this.options
    if (this?.dataset?.options) {
      const dataOptions = JSON.parse(this.dataset.options);
      this.options = {
        ...this.options,
        ...dataOptions,
      };
    }

    // For the menu drawer, filters, cart drawer and search, define the content they expand
    if (this.options.classToToggle === window.drawerToggleClasses.cartDrawer) this.targetID = 'cartDrawer';
    if (this.options.classToToggle === window.drawerToggleClasses.filters) this.targetID = 'CollectionFiltersForm';

    // for the mobile menu, define the collapsible menu so we can close it, otherwise the wrong pane might be visible upon opening
    if (this.options.classToToggle === window.drawerToggleClasses.mainMenu) {
      this.targetID = 'mainMenu';
      this.navComponent = document.getElementById(this.targetID).querySelector('navigation-component');
    }

    // For the search modal, close other modals
    if (this.options.classToToggle === window.drawerToggleClasses.headerSearch) {
      this.targetID = 'SearchModal';
      document.body.classList.remove(window.drawerToggleClasses.mainMenu);
    }

    // prevent scroll for any of the modals
    if (this.targetID) this.preventBackgroundScroll = true;

    // All toggle components should have a button for accessibility purposes
    this.button = document.querySelector(`[aria-controls="${this.targetID}"`) || this.querySelector('button') || null;
    this.target = document.querySelector(this.options.target);

    if (!this.target) return;

    this.listeners = {
      handleClick: this.handleClick.bind(this),
      keyUpCloseEvent: this.keyUpCloseEvent.bind(this),
      handleMouseEnter: debounce(() => this.add(), this.options.hoverDelay),
      handleMouseLeave: debounce(() => this.remove(), this.options.hoverDelay),
    };

    // Add event listeners
    this.addEventListener('click', this.listeners.handleClick);

    if (this.options.onHover) {
      this.addEventListener('mouseenter', this.listeners.handleMouseEnter);
      this.addEventListener('mouseleave', this.listeners.handleMouseLeave);
    }
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.listeners.handleClick);

    if (this.options.onHover) {
      this.removeEventListener('mouseenter', this.listeners.handleMouseEnter);
      this.removeEventListener('mouseleave', this.listeners.handleMouseLeave);
    }
    document.removeEventListener('keyup', this.listeners.keyUpCloseEvent);
  }

  handleClick(event) {
    if (!event) return;
    event.preventDefault();
    event.stopPropagation();
    this.toggle();

  }

  keyUpCloseEvent(event) {
    if (event?.code?.toUpperCase() !== 'ESCAPE') return;

    if (!document.documentElement.classList.contains('with-fancybox')) {
      this.remove();
    }

    removeTrapFocus(this.button);
  }

  add() {
    // add the event listener to close the modal again
    document.addEventListener('keyup', this.listeners.keyUpCloseEvent);

    if (this.target) this.target.classList.add(this.options.classToToggle);

    if (this.targetID) {
      document.dispatchEvent(new CustomEvent('toggle-opened', {
        bubbles: true,
        detail: {
          id: this.targetID,
        },
      }));

      document.querySelectorAll(`button[aria-controls="${this.targetID}"]`)?.forEach(button => button.setAttribute('aria-expanded', true));

      if (this.targetID === 'mainMenu' && window.windowWidth >= 1024) {
        // this.navComponent?.open(this.navComponent.groups[0]);
      }
      else {
        const focusTarget = document.getElementById(this.targetID);
        const elementToFocus = (this.targetID === 'SearchModal') ? focusTarget.querySelector('input') : focusTarget;

        trapFocus(focusTarget, elementToFocus);
      }
    }

    if (this.options.ariaExpanded && this.button) this.button.setAttribute('aria-expanded', true);

    if (this.preventBackgroundScroll) scrollLock.lock();
  }

  remove() {
    if (this.target) this.target.classList.remove(this.options.classToToggle);

    // Switch aria-expanded
    if (this.options.ariaExpanded && this.button) this.button.setAttribute('aria-expanded', false);

    // remove the trap focus
    if (this.targetID) {
      removeTrapFocus(this.button);

      // dispatch a custom event so we can target specific logic based on the ID
      document.dispatchEvent(new CustomEvent('toggle-closed', {
        bubbles: true,
        detail: {
          id: this.targetID,
        },
      }));

      this.navComponent?.closeAll();

      // Close the predictive search
      const predictiveSearch = document.querySelector('predictive-search');
      if (predictiveSearch && this.options.classToToggle === window.drawerToggleClasses.headerSearch) predictiveSearch.close();

      document.querySelectorAll(`button[aria-controls="${this.targetID}"]`)?.forEach(button => button.setAttribute('aria-expanded', false));

      // return the focus to the trigger
      this.button?.focus();
    }

    // Remove class from the document
    if (this.preventBackgroundScroll) scrollLock.unlock();
  }

  toggle() {
    if (!this.target) return;
    if (!this.target.classList.contains(this.options.classToToggle)) {
      this.add();
    }
    else {
      this.remove();
    }
  }
}

if (!customElements.get('class-toggle-component')) {
  customElements.define('class-toggle-component', ClassToggleComponent);
}